type FocusMode = "none" | "manual" | "single-shot" | "continuous";

interface DotNetReference {
    invokeMethodAsync(methodName: string, ...args: unknown[]): Promise<unknown>;
}

interface CameraPoint {
    x: number;
    y: number;
}

interface CameraRange {
    min: number;
    max: number;
    step: number;
}

type CameraCapabilities = MediaTrackCapabilities & {
    focusMode?: string[];
    focusDistance?: CameraRange;
};

type CameraSettings = MediaTrackSettings & {
    focusMode?: string;
    focusDistance?: number;
};

type CameraConstraintSet = MediaTrackConstraintSet & {
    focusMode?: FocusMode;
    pointsOfInterest?: CameraPoint[];
};

type CameraSupportedConstraints = MediaTrackSupportedConstraints & {
    pointsOfInterest?: boolean;
};

/**
 * Provides camera frames for barcode decoding in .NET.
 */
export class BarcodeScanner {
    private videoElement: HTMLVideoElement | null = null;
    private videoStream: MediaStream | null = null;
    private dotNetReference: DotNetReference | null = null;
    private captureTimeout: number | null = null;
    private isProcessingFrame = false;
    private sessionId = 0;

    /**
     * Starts scanning, preferring an identifiable primary rear camera.
     * @param dotNetRef Reference used to forward captured frames to .NET.
     */
    public async StartScanner(dotNetRef: DotNetReference): Promise<void> {
        this.StopScanner();
        const sessionId = this.sessionId;

        this.dotNetReference = dotNetRef;
        this.videoElement = document.querySelector<HTMLVideoElement>(".barcode-scanner video");

        if (this.videoElement === null) {
            console.error("Barcode scanner video element was not found.");
            return;
        }

        if (navigator.mediaDevices?.getUserMedia === undefined) {
            alert("Camera is not available.");
            return;
        }

        try {
            // Prefer 1080p for barcode detail; 'ideal' allows the browser to negotiate another size.
            const cameraConstraints: MediaTrackConstraints = {
                facingMode: { ideal: "environment" },
                width: { ideal: 1920 },
                height: { ideal: 1080 }
            };

            let stream = await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: cameraConstraints
            });

            // A stopped or replaced session must release cameras acquired after cancellation.
            if (sessionId !== this.sessionId) {
                this.StopStream(stream);
                return;
            }

            this.videoStream = stream;
            const devices = await this.GetAvailableCameraDevices();

            if (sessionId !== this.sessionId) {
                this.StopStream(stream);
                return;
            }

            stream = await this.PreferPrimaryRearCamera(stream, devices, cameraConstraints, sessionId);

            if (sessionId !== this.sessionId) {
                this.StopStream(stream);
                return;
            }

            this.videoStream = stream;
            this.videoElement.srcObject = stream;
            await this.videoElement.play();

            if (sessionId !== this.sessionId) {
                this.StopStream(stream);
                return;
            }

            const videoTrack = this.GetVideoTrack();

            if (videoTrack === null) {
                throw new Error("The camera stream does not contain a video track.");
            }

            this.ShowDeviceInformation(videoTrack, "Camera initialized");
            this.ShowAvailableCameras(devices, videoTrack);
            await this.ConfigureFocus(videoTrack);

            if (sessionId !== this.sessionId) {
                return;
            }

            this.ShowDeviceInformation(videoTrack, "Camera configured");
            this.videoElement.addEventListener("click", this.HandleVideoClick);
            this.captureTimeout = window.setTimeout(() => void this.CaptureNextFrame(sessionId), 250);
        } catch (error) {
            // An older session must not tear down a newer scanner session.
            if (sessionId !== this.sessionId) {
                return;
            }

            console.error("Could not initialize barcode scanner.", error);
            this.StopScanner();

            alert("Could not access the camera. Please try again.");
        }
    }

    /**
     * Forwards one frame to .NET, allowing only one pending decode per session.
     */
    public async CaptureFrame(): Promise<void> {
        if (this.isProcessingFrame === true ||
            this.videoStream === null ||
            this.videoElement === null ||
            this.dotNetReference === null) {
            return;
        }

        const sessionId = this.sessionId;
        const dotNetReference = this.dotNetReference;
        this.isProcessingFrame = true;

        try {
            const width = this.videoElement.videoWidth;
            const height = this.videoElement.videoHeight;

            if (width <= 0 || height <= 0) {
                return;
            }

            const canvas = document.querySelector<HTMLCanvasElement>(".barcode-scanner canvas");

            if (canvas === null) {
                console.error("Barcode scanner canvas element was not found.");
                return;
            }

            const context = canvas.getContext("2d");

            if (context === null) {
                console.error("Could not create barcode scanner canvas context.");
                return;
            }

            // Setting a canvas dimension resets its drawing buffer, even when unchanged.
            if (canvas.width !== width) {
                canvas.width = width;
            }

            if (canvas.height !== height) {
                canvas.height = height;
            }
            context.drawImage(this.videoElement, 0, 0, width, height);

            const base64 = canvas.toDataURL("image/png");
            await dotNetReference.invokeMethodAsync("ProcessImage", base64, width, height);
        } catch (error) {
            console.error("Could not process barcode scanner frame.", error);
        } finally {
            // A previous session's decode must not release the current session's lock.
            if (sessionId === this.sessionId) {
                this.isProcessingFrame = false;
            }
        }
    }

    /**
     * Stops the scanner and releases camera and event resources.
     */
    public StopScanner(): void {
        // Invalidate pending asynchronous work from the current session.
        this.sessionId++;

        if (this.captureTimeout !== null) {
            window.clearTimeout(this.captureTimeout);
            this.captureTimeout = null;
        }

        if (this.videoElement !== null) {
            this.videoElement.removeEventListener("click", this.HandleVideoClick);
            this.videoElement.pause();
            this.videoElement.srcObject = null;
        }

        if (this.videoStream !== null) {
            this.StopStream(this.videoStream);
        }

        this.videoStream = null;
        this.videoElement = null;
        this.dotNetReference = null;
        this.isProcessingFrame = false;
    }

    /**
     * Schedule after decoding completes to avoid polling during a pending .NET call
     * and waiting for another fixed interval tick once a slow decode has finished.
     */
    private async CaptureNextFrame(sessionId: number): Promise<void> {
        if (sessionId !== this.sessionId) {
            return;
        }

        this.captureTimeout = null;
        const startedAt = performance.now();
        await this.CaptureFrame();

        if (sessionId !== this.sessionId) {
            return;
        }

        // Keep at least 250 ms between starts; a slow decode needs no additional delay.
        const delay = Math.max(0, 250 - (performance.now() - startedAt));
        this.captureTimeout = window.setTimeout(() => void this.CaptureNextFrame(sessionId), delay);
    }

    private readonly HandleVideoClick = async (event: MouseEvent): Promise<void> => {
        const videoTrack = this.GetVideoTrack();

        if (videoTrack === null || this.videoElement === null) {
            return;
        }

        const rect = this.videoElement.getBoundingClientRect();

        if (rect.width <= 0 || rect.height <= 0) {
            return;
        }

        // Camera points of interest use normalized coordinates from 0 to 1.
        const point: CameraPoint = {
            x: this.Clamp((event.clientX - rect.left) / rect.width, 0, 1),
            y: this.Clamp((event.clientY - rect.top) / rect.height, 0, 1)
        };

        await this.FocusAtPoint(videoTrack, point);
    };

    private async ConfigureFocus(videoTrack: MediaStreamTrack): Promise<void> {
        if (typeof videoTrack.getCapabilities !== "function") {
            console.debug("Camera capabilities are not available.");
            return;
        }

        const capabilities = videoTrack.getCapabilities() as CameraCapabilities;
        const settings = videoTrack.getSettings() as CameraSettings;
        const focusModes = capabilities.focusMode ?? [];

        // Some Android cameras report active continuous focus but advertise only manual focus.
        if (settings.focusMode === "continuous" && focusModes.includes("continuous") === false) {
            console.debug("Camera already uses continuous autofocus.", {
                advertisedFocusModes: focusModes,
                activeFocusMode: settings.focusMode,
                focusDistance: settings.focusDistance
            });
            return;
        }

        if (focusModes.includes("continuous") === true) {
            // Reassert the supported mode: reported settings do not prove focus has converged.
            await this.TryApplyCameraConstraints(videoTrack, { focusMode: "continuous" }, "continuous autofocus");
            return;
        }

        // Manual focus distance depends on the lens; retain the default rather than guessing.
        if (focusModes.includes("manual") === true && capabilities.focusDistance !== undefined) {
            console.debug("Camera exposes manual focus but no automatic focus mode. Keeping the browser/camera default.", {
                focusDistanceRange: capabilities.focusDistance,
                currentFocusDistance: settings.focusDistance,
                activeFocusMode: settings.focusMode
            });
            return;
        }

        console.debug("Camera does not expose a configurable autofocus mode.", {
            advertisedFocusModes: focusModes,
            activeFocusMode: settings.focusMode
        });
    }

    private async FocusAtPoint(videoTrack: MediaStreamTrack, point: CameraPoint): Promise<void> {
        if (typeof videoTrack.getCapabilities !== "function") {
            return;
        }

        const capabilities = videoTrack.getCapabilities() as CameraCapabilities;
        const settings = videoTrack.getSettings() as CameraSettings;
        const supportedConstraints = navigator.mediaDevices.getSupportedConstraints() as CameraSupportedConstraints;
        const focusModes = capabilities.focusMode ?? [];
        const constraints: CameraConstraintSet = {};

        if (supportedConstraints.pointsOfInterest === true) {
            constraints.pointsOfInterest = [point];
        }

        // Preserve active continuous focus even when it is not advertised as a capability.
        if (settings.focusMode !== "continuous") {
            if (focusModes.includes("single-shot") === true) {
                constraints.focusMode = "single-shot";
            } else if (focusModes.includes("continuous") === true) {
                constraints.focusMode = "continuous";
            } else if (focusModes.includes("manual") === true) {
                console.debug("Tap-to-focus camera exposes manual focus only.", {
                    point,
                    focusDistanceRange: capabilities.focusDistance,
                    currentFocusDistance: settings.focusDistance,
                    activeFocusMode: settings.focusMode
                });
            }
        }

        if (Object.keys(constraints).length === 0) {
            console.debug("Camera does not expose controllable tap-to-focus.", { point, capabilities, settings });
            return;
        }

        await this.TryApplyCameraConstraints(videoTrack, constraints, "tap-to-focus");
    }

    /**
     * Unsupported camera controls must not interrupt scanning.
     */
    private async TryApplyCameraConstraints(
        videoTrack: MediaStreamTrack,
        constraints: CameraConstraintSet,
        description: string
    ): Promise<void> {
        try {
            await videoTrack.applyConstraints({ advanced: [constraints] });
            console.debug(`Completed ${description} request; verify settings and preview.`, {
                requested: constraints,
                settings: videoTrack.getSettings()
            });
        } catch (error) {
            console.warn(`Could not apply ${description}.`, {
                requested: constraints,
                settings: videoTrack.getSettings(),
                error
            });
        }
    }

    private GetVideoTrack(): MediaStreamTrack | null {
        return this.videoStream?.getVideoTracks()[0] ?? null;
    }

    private async GetAvailableCameraDevices(): Promise<MediaDeviceInfo[]> {
        try {
            // Enumerate after camera permission is granted so labels are available.
            const devices = await navigator.mediaDevices.enumerateDevices();
            return devices.filter(device => device.kind === "videoinput");
        } catch (error) {
            console.debug("Keeping the default camera; enumeration failed.", error);
            return [];
        }
    }

    /**
     * Choose the lens independently of the requested stream resolution. The highest reported
     * camera resolution can belong to a lens poorly suited to close-range barcode scanning.
     * Camera 0 in Android-style rear-camera labels is a heuristic for the primary lens.
     * Unrecognized labels retain the browser's selection.
     */
    private async PreferPrimaryRearCamera(
        stream: MediaStream,
        devices: MediaDeviceInfo[],
        constraints: MediaTrackConstraints,
        sessionId: number
    ): Promise<MediaStream> {
        const selectedTrack = stream.getVideoTracks()[0];
        const selectedSettings = selectedTrack?.getSettings();
        const rearCameras = devices.filter(device => /\bfacing back\b/i.test(device.label));
        const primaryCamera = rearCameras.find(device => /^camera(?:2)?\s+0\s*,/i.test(device.label));

        if (rearCameras.length < 2 ||
            primaryCamera === undefined ||
            selectedSettings?.deviceId === undefined ||
            selectedSettings.facingMode !== "environment" ||
            primaryCamera.deviceId === selectedSettings.deviceId) {
            return stream;
        }

        this.WriteDiagnosticOutput("Camera selection", {
            previous: selectedTrack.label,
            preferred: primaryCamera.label,
            reason: "Prefer camera 0 among multiple labelled rear cameras"
        });

        // Mobile devices may reject a second camera while the original is still open.
        this.StopStream(stream);

        try {
            return await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: { ...constraints, deviceId: { exact: primaryCamera.deviceId } }
            });
        } catch (error) {
            if (sessionId !== this.sessionId) {
                return stream;
            }

            console.warn("Could not open the preferred camera; reopening the original.", error);
            this.WriteDiagnosticOutput("Camera selection fallback", { label: selectedTrack.label });

            return await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: { ...constraints, deviceId: { exact: selectedSettings.deviceId } }
            });
        }
    }

    private StopStream(stream: MediaStream): void {
        stream.getTracks().forEach(track => track.stop());
    }

    private ShowDeviceInformation(videoTrack: MediaStreamTrack, stage: string): void {
        try {
            this.WriteDiagnosticOutput(stage, {
                userAgent: navigator.userAgent,
                label: videoTrack.label,
                capabilities: typeof videoTrack.getCapabilities === "function" ? videoTrack.getCapabilities() : null,
                constraints: videoTrack.getConstraints(),
                settings: videoTrack.getSettings()
            });
        } catch (error) {
            console.warn("Could not read barcode scanner diagnostics.", error);
        }
    }

    private ShowAvailableCameras(devices: MediaDeviceInfo[], selectedTrack: MediaStreamTrack): void {
        try {
            const selectedSettings = selectedTrack.getSettings();
            const cameras = devices.map((device, index) => ({
                index,
                label: device.label || `Camera ${index + 1}`,
                deviceId: device.deviceId,
                groupId: device.groupId,
                selected: device.deviceId === selectedSettings.deviceId
            }));

            this.WriteDiagnosticOutput("Available cameras", cameras);
        } catch (error) {
            console.warn("Could not read available camera diagnostics.", error);
        }
    }

    /**
     * Writes camera diagnostics to the developer console.
     */
    private WriteDiagnosticOutput(stage: string, information: unknown): void {
        console.debug(`Barcode scanner: ${stage}`, information);
    }

    private Clamp(value: number, min: number, max: number): number {
        return Math.min(max, Math.max(min, value));
    }
}

(window as Window & {
    InitBarcodeScanner?: () => BarcodeScanner;
}).InitBarcodeScanner = function () {
    return new BarcodeScanner();
};
